import type { TicketQuestionAnswer } from "./useGetTicketQuestionAnswers";

type SelectQuestionAnswer = Extract<TicketQuestionAnswer, { type: "SELECT" }>;
type TextQuestionAnswer = Extract<TicketQuestionAnswer, { type: "TEXT" }>;
type QuestionnaireAnswerCount = SelectQuestionAnswer["answerCounts"][number];

type QuestionnaireQuestionDataCommon = {
  questionId: string;
  text: string;
  sourceEventIds: string[];
  displayOrder: number;
  questionLabel: string;
};

export type BinarySelectQuestionData = QuestionnaireQuestionDataCommon & {
  displayType: "binarySelect";
  totalAnswerCount: number;
  accentAnswer?: string;
  answers: {
    answer: string;
    count: number;
    percentage: number;
    isAccent: boolean;
  }[];
  ticketAnswerRates: {
    ticket: SelectQuestionAnswer["ticketAnswerAggregations"][number]["ticket"];
    respondentCount: number;
    accentAnswerCount: number;
    percentage: number;
  }[];
};

export type RankedSelectQuestionData = QuestionnaireQuestionDataCommon & {
  displayType: "rankedSelect";
  totalAnswerCount: number;
  optionCount: number;
  answerKindCount: number;
  answers: {
    rank: number;
    answer: string;
    count: number;
    percentage: number;
  }[];
};

export type FreeTextQuestionData = QuestionnaireQuestionDataCommon & {
  displayType: "freeText";
  answerDetails: (TextQuestionAnswer["answerDetails"][number] & {
    answerLabel: string;
  })[];
};

export type PurchaserQuestionnaireQuestionData =
  | BinarySelectQuestionData
  | RankedSelectQuestionData
  | FreeTextQuestionData;

export const createPurchaserQuestionnaireData = (
  questions: readonly TicketQuestionAnswer[]
): PurchaserQuestionnaireQuestionData[] =>
  questions.map((question, index) => {
    const commonData = {
      questionId: question.questionId,
      text: question.text,
      sourceEventIds: [...question.sourceEventIds],
      displayOrder: question.displayOrder,
      questionLabel: `Q${index + 1}`,
    };

    if (question.type === "TEXT") {
      return createFreeTextQuestionData(question, commonData);
    }

    const answerCounts = getActualAnswerCounts(question.answerCounts);

    return answerCounts.length === 2
      ? createBinarySelectQuestionData(question, answerCounts, commonData)
      : createRankedSelectQuestionData(question, answerCounts, commonData);
  });

const createBinarySelectQuestionData = (
  question: SelectQuestionAnswer,
  answerCounts: QuestionnaireAnswerCount[],
  commonData: QuestionnaireQuestionDataCommon
): BinarySelectQuestionData => {
  const orderedAnswerCounts = sortAnswerCountsByOptionOrder(
    answerCounts,
    question.options
  );
  const totalAnswerCount = getTotalAnswerCount(orderedAnswerCounts);
  const actualAnswers = new Set(
    orderedAnswerCounts.map(({ answer }) => answer)
  );
  const accentAnswer = question.options.find((option) =>
    actualAnswers.has(option)
  );

  return {
    ...commonData,
    displayType: "binarySelect",
    totalAnswerCount,
    accentAnswer,
    answers: orderedAnswerCounts.map(({ answer, count }) => ({
      answer,
      count,
      percentage: getPercentage(count, totalAnswerCount),
      isAccent: answer === accentAnswer,
    })),
    ticketAnswerRates: question.ticketAnswerAggregations.map(
      ({ ticket, respondentCount, answerCounts: ticketAnswerCounts }) => {
        const normalizedRespondentCount = getPositiveCount(respondentCount);
        const accentAnswerCount =
          accentAnswer === undefined
            ? 0
            : getPositiveCount(
                ticketAnswerCounts.find(({ answer }) => answer === accentAnswer)
                  ?.count ?? 0
              );

        return {
          ticket: { ...ticket },
          respondentCount: normalizedRespondentCount,
          accentAnswerCount,
          percentage: getPercentage(
            accentAnswerCount,
            normalizedRespondentCount
          ),
        };
      }
    ),
  };
};

const createRankedSelectQuestionData = (
  question: SelectQuestionAnswer,
  answerCounts: QuestionnaireAnswerCount[],
  commonData: QuestionnaireQuestionDataCommon
): RankedSelectQuestionData => {
  const totalAnswerCount = getTotalAnswerCount(answerCounts);
  const optionOrder = createOptionOrder(question.options);
  const rankedAnswerCounts = answerCounts
    .map((answerCount, index) => ({ answerCount, index }))
    .sort((left, right) => {
      const countDifference = right.answerCount.count - left.answerCount.count;

      if (countDifference !== 0) {
        return countDifference;
      }

      const optionOrderDifference =
        getOptionOrder(left.answerCount.answer, optionOrder) -
        getOptionOrder(right.answerCount.answer, optionOrder);

      return optionOrderDifference === 0
        ? left.index - right.index
        : optionOrderDifference;
    })
    .map(({ answerCount }) => answerCount);

  return {
    ...commonData,
    displayType: "rankedSelect",
    totalAnswerCount,
    optionCount: question.options.length,
    answerKindCount: rankedAnswerCounts.length,
    answers: rankedAnswerCounts.map(({ answer, count }, index) => ({
      rank: index + 1,
      answer,
      count,
      percentage: getPercentage(count, totalAnswerCount),
    })),
  };
};

const createFreeTextQuestionData = (
  question: TextQuestionAnswer,
  commonData: QuestionnaireQuestionDataCommon
): FreeTextQuestionData => ({
  ...commonData,
  displayType: "freeText",
  answerDetails: question.answerDetails
    .filter(({ answer }) => answer.trim().length > 0)
    .map(({ tickets, ...answerDetail }, index) => ({
      ...answerDetail,
      tickets: tickets.map((ticket) => ({ ...ticket })),
      answerLabel: `#${index + 1}`,
    })),
});

const getActualAnswerCounts = (
  answerCounts: readonly QuestionnaireAnswerCount[]
): QuestionnaireAnswerCount[] =>
  answerCounts.filter(
    ({ answer, count }) =>
      answer.trim().length > 0 && Number.isFinite(count) && count > 0
  );

const getTotalAnswerCount = (
  answerCounts: readonly QuestionnaireAnswerCount[]
): number => answerCounts.reduce((total, { count }) => total + count, 0);

const getPercentage = (count: number, totalCount: number): number => {
  if (totalCount <= 0) {
    return 0;
  }

  return (count / totalCount) * 100;
};

const getPositiveCount = (count: number): number => {
  if (!Number.isFinite(count) || count <= 0) {
    return 0;
  }

  return count;
};

const sortAnswerCountsByOptionOrder = (
  answerCounts: readonly QuestionnaireAnswerCount[],
  options: readonly string[]
): QuestionnaireAnswerCount[] => {
  const optionOrder = createOptionOrder(options);

  return answerCounts
    .map((answerCount, index) => ({ answerCount, index }))
    .sort((left, right) => {
      const orderDifference =
        getOptionOrder(left.answerCount.answer, optionOrder) -
        getOptionOrder(right.answerCount.answer, optionOrder);

      return orderDifference || left.index - right.index;
    })
    .map(({ answerCount }) => answerCount);
};

const createOptionOrder = (options: readonly string[]): Map<string, number> => {
  const optionOrder = new Map<string, number>();

  options.forEach((option, index) => {
    if (!optionOrder.has(option)) {
      optionOrder.set(option, index);
    }
  });

  return optionOrder;
};

const getOptionOrder = (
  answer: string,
  optionOrder: ReadonlyMap<string, number>
): number => optionOrder.get(answer) ?? Number.MAX_SAFE_INTEGER;
